import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getServiceMetadata,
    updateOtherStandardSchedule,
} from '@/modules/Service/services'
import {
    ServiceStatus,
    type ServiceInformationProps,
} from '@/modules/Service/types'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading } from 'vnetwork-ui'
import type {
    OtherStandardPaymentRange,
    OtherStandardSchedule,
} from '../Information/other-standard-config'
import {
    dateValue,
    emptyRange,
    getFallbackRanges,
    getOtherStandardSchedule,
    nextRangeFrom,
    normalizeSchedule,
} from './utils'

export const useOtherStandardConfig = (
    service: ServiceInformationProps['service']
) => {
    const { t } = useTranslation('services')
    const { isAdmin } = usePermissionsStore()
    const canEdit =
        isAdmin && service.service_status === ServiceStatus.WaitingConfirm

    const {
        data: metadata,
        renderState,
        refetch,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_METADATA, service.service_id],
        queryFn: () => getServiceMetadata(service.service_id),
        loadingComponent: (
            <div className="flex justify-center py-8">
                <Loading.CircularProgress width={30} height={30} />
            </div>
        ),
    })

    const schedule = useMemo(
        () =>
            getOtherStandardSchedule(
                metadata?.metadata as Record<string, any> | undefined
            ),
        [metadata]
    )

    const [draft, setDraft] = useState<OtherStandardSchedule>({
        payment_method: 'prepaid',
        payment_mode: 'one_time',
        description: '',
        ranges: [emptyRange()],
    })
    const [initialDraft, setInitialDraft] = useState<OtherStandardSchedule>({
        payment_method: 'prepaid',
        payment_mode: 'one_time',
        description: '',
        ranges: [emptyRange()],
    })

    useEffect(() => {
        const ranges =
            schedule?.ranges && schedule.ranges.length > 0
                ? schedule.ranges
                : getFallbackRanges(service)

        const nextDraft = {
            payment_method: schedule?.payment_method || 'prepaid',
            payment_mode:
                schedule?.payment_mode ||
                (ranges.length > 1 ? 'multiple_times' : 'one_time'),
            description: schedule?.description || '',
            ranges: ranges.length > 0 ? ranges : [emptyRange()],
        }

        setDraft(nextDraft)
        setInitialDraft(nextDraft)
    }, [schedule, service])

    const updateMutation = useMutationWithHandler(
        {
            mutationFn: updateOtherStandardSchedule,
        },
        {
            successMessage: t('update-metadata-success'),
            reactQueryKeys: [
                QUERY_KEYS.SERVICE_DETAIL,
                QUERY_KEYS.SERVICES_METADATA,
            ],
            onSuccess: () => {
                setInitialDraft(draft)
                refetch()
            },
        }
    )

    const hasChanges = useMemo(
        () => normalizeSchedule(draft) !== normalizeSchedule(initialDraft),
        [draft, initialDraft]
    )
    const canSave = canEdit && hasChanges && !updateMutation.isPending

    const updateRange = (
        index: number,
        key: keyof OtherStandardPaymentRange,
        value: string | number
    ) => {
        setDraft((current) => ({
            ...current,
            ranges:
                current.ranges?.map((range, rangeIndex) =>
                    rangeIndex === index ? { ...range, [key]: value } : range
                ) || [],
        }))
    }

    const updatePaymentMode = (paymentMode: string) => {
        setDraft((current) => ({
            ...current,
            payment_mode: paymentMode,
            ranges:
                paymentMode === 'one_time'
                    ? [current.ranges?.[0] || emptyRange()]
                    : current.ranges && current.ranges.length > 1
                      ? current.ranges
                      : [
                            current.ranges?.[0] || emptyRange(),
                            nextRangeFrom(current.ranges?.[0]),
                        ],
        }))
    }

    const addRange = () => {
        setDraft((current) => ({
            ...current,
            payment_mode: 'multiple_times',
            ranges: [
                ...(current.ranges || []),
                nextRangeFrom(current.ranges?.[current.ranges.length - 1]),
            ],
        }))
    }

    const removeRange = (index: number) => {
        setDraft((current) => {
            const nextRanges =
                current.ranges?.filter(
                    (_, rangeIndex) => rangeIndex !== index
                ) || []
            return {
                ...current,
                payment_mode:
                    nextRanges.length > 1 ? 'multiple_times' : 'one_time',
                ranges: nextRanges.length > 0 ? nextRanges : [emptyRange()],
            }
        })
    }

    const save = () => {
        const activeRanges =
            draft.payment_mode === 'one_time'
                ? (draft.ranges || []).slice(0, 1)
                : draft.ranges || []
        const ranges = activeRanges.map((range) => ({
            start_date: dateValue(range.start_date),
            end_date: dateValue(range.end_date),
            price: Number(range.price || 0),
        }))

        updateMutation.mutate({
            serviceID: service.service_id,
            payment_method: draft.payment_method || 'prepaid',
            payment_mode: draft.payment_mode || 'one_time',
            description: draft.description || '',
            ranges,
        })
    }

    return {
        canEdit,
        canSave,
        draft,
        renderState,
        setDraft,
        updateRange,
        updatePaymentMode,
        addRange,
        removeRange,
        save,
    }
}
